import { useState, useRef } from 'react'
import { useGlobalProgressStore } from '../../stores/globalProgressStore'
import { Video, X, Play } from 'lucide-react'
import { ProductVideo, getValidAuthToken } from '../../services/api'

interface VideoUploaderProps {
  productId: string
  existingVideos?: ProductVideo[]
  onVideosChange?: (videos: ProductVideo[]) => void
  maxVideos?: number
}

export default function VideoUploader({
  productId,
  existingVideos = [],
  onVideosChange,
  maxVideos = 2
}: VideoUploaderProps) {
  const [videos, setVideos] = useState<ProductVideo[]>(existingVideos)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setGlobalProgress } = useGlobalProgressStore()

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Check if we can add more videos
    if (videos.length >= maxVideos) {
      alert(`Maximum ${maxVideos} videos allowed`)
      return
    }

    const file = files[0]

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg']
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid video type. Allowed: MP4, WebM, MOV, AVI, MPEG')
      return
    }

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      alert('Video is too large. Maximum size is 100MB')
      return
    }

    setUploading(true)
    setGlobalProgress({ isActive: true, message: 'Uploading video...', progress: 0 })

    try {
      const formData = new FormData()
      formData.append('video', file)

      // Get auth token
      const token = await getValidAuthToken()
      if (!token) {
        alert('Not authenticated')
        setUploading(false)
        setGlobalProgress({ isActive: false })
        return
      }

      // Upload video
      const API_BASE_URL = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/v1`
        : 'https://api.shop.bg502.ru/api/v1'

      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100)
          setGlobalProgress({ isActive: true, message: `Uploading video... ${progress}%`, progress })
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText)
            const newVideos = [...videos, result.video]
            setVideos(newVideos)
            onVideosChange?.(newVideos)
            setGlobalProgress({ isActive: false })
          } catch (err) {
            console.error('Failed to parse response:', err)
            alert('Upload failed: Invalid response')
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText)
            alert(error.message || 'Upload failed')
          } catch {
            alert(`Upload failed: ${xhr.status}`)
          }
        }
        setUploading(false)
        setGlobalProgress({ isActive: false })
      })

      xhr.addEventListener('error', () => {
        alert('Network error during upload')
        setUploading(false)
        setGlobalProgress({ isActive: false })
      })

      xhr.open('POST', `${API_BASE_URL}/admin/products/${productId}/videos`)
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.send(formData)
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload video')
      setUploading(false)
      setGlobalProgress({ isActive: false })
    }

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (videoId: string) => {
    if (!confirm('Delete this video?')) return

    try {
      const token = await getValidAuthToken()
      if (!token) {
        alert('Not authenticated')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/v1`
        : 'https://api.shop.bg502.ru/api/v1'

      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/videos/${videoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete')
      }

      const newVideos = videos.filter(v => v.id !== videoId)
      setVideos(newVideos)
      onVideosChange?.(newVideos)
    } catch (error) {
      console.error('Delete error:', error)
      alert('Failed to delete video')
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return ''
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Video className="w-4 h-4" />
          Product Videos ({videos.length}/{maxVideos})
        </label>
        {videos.length < maxVideos && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-sm bg-purple-500 text-white px-3 py-1 rounded-lg disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Add Video'}
          </button>
        )}
      </div>

      {/* Video List */}
      <div className="space-y-3">
        {videos.map((video, index) => (
          <div
            key={video.id}
            className="relative group border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-900"
          >
            <video
              src={video.video_url}
              className="w-full h-40 object-cover"
              controls
              preload="metadata"
            />

            {/* Video Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
              <div className="flex items-center justify-between text-white text-xs">
                <span className="flex items-center gap-1">
                  <Play className="w-3 h-3" />
                  Video {index + 1}
                </span>
                {video.duration_seconds && (
                  <span>{formatDuration(video.duration_seconds)}</span>
                )}
              </div>
            </div>

            {/* Delete Button */}
            <button
              type="button"
              onClick={() => handleDelete(video.id)}
              className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* Add Video Placeholder */}
        {videos.length < maxVideos && !uploading && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center hover:border-purple-400 transition-colors w-full"
          >
            <Video className="w-8 h-8 text-gray-400 mb-2" />
            <div className="text-sm text-gray-500">Add Video</div>
            <div className="text-xs text-gray-400">Max 100MB</div>
          </button>
        )}

        {/* Uploading State */}
        {uploading && (
          <div className="border-2 border-purple-300 rounded-lg h-32 flex items-center justify-center bg-purple-50">
            <div className="text-center">
              <div className="animate-pulse text-2xl mb-2">
                <Video className="w-8 h-8 text-purple-500 mx-auto" />
              </div>
              <div className="text-sm text-purple-600">Uploading video...</div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        Supports MP4, WebM, MOV, AVI, MPEG (max 100MB each, max {maxVideos} videos).
      </p>
    </div>
  )
}
